import {
  useQuery,
  NetworkStatus,
  DocumentNode,
  TypedDocumentNode,
  QueryHookOptions,
} from '@apollo/client';
import { useCallback } from 'react';

const ITEMS_PER_PAGE = 10;

interface Connection<T = unknown> {
  __typename?: string;
  collection: T[];
  pagination: Partial<Pagination>;
}

type InfiniteQueryVariables = Omit<Partial<Pagination>, '__typename'> &
  Record<string, unknown>;

export interface UseInfiniteQueryOptions<
  TData,
  TVariables extends InfiniteQueryVariables,
> extends QueryHookOptions<TData, TVariables> {
  fieldName: keyof TData;
  perPage?: number;
}

export function useInfiniteQuery<
  TData = Record<string, Connection>,
  TVariables extends InfiniteQueryVariables = InfiniteQueryVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  { fieldName, ...options }: UseInfiniteQueryOptions<TData, TVariables>,
) {
  const { variables = {} as TVariables, ...rest } = options;

  const { data, error, fetchMore, refetch, networkStatus } = useQuery<
    TData,
    TVariables
  >(query, {
    variables: {
      ...variables,
      page: variables.page ?? 1,
      perPage: variables.perPage ?? ITEMS_PER_PAGE,
    } as TVariables,
    notifyOnNetworkStatusChange: true,
    ...rest,
  });

  const connection = data?.[fieldName] as Connection | undefined;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const isLoading = networkStatus === NetworkStatus.loading;
  const isSettingVariables = networkStatus === NetworkStatus.setVariables;
  const loading = isLoading || isSettingVariables;

  const currentPage = connection?.pagination.page ?? 1;
  const totalPages = connection?.pagination.totalPages ?? 1;

  const hasNextPage = currentPage < totalPages;

  const loadMore = useCallback(() => {
    if (!hasNextPage) return undefined;

    return fetchMore({
      variables: {
        page: currentPage + 1,
        perPage: variables.perPage ?? ITEMS_PER_PAGE,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prevResult;

        const prevConnection = prevResult[fieldName] as Connection;
        const nextConnection = fetchMoreResult[fieldName] as Connection;

        return {
          ...prevResult,
          [fieldName]: {
            __typename: prevConnection.__typename,
            collection: [
              ...prevConnection.collection,
              ...nextConnection.collection,
            ],
            pagination: nextConnection.pagination,
          },
        } as TData;
      },
    });
  }, [fetchMore, fieldName, currentPage, hasNextPage, variables]);

  return {
    data,
    hasNextPage,
    loading,
    loadingMore,
    error,
    refetch,
    loadMore,
  };
}
